import type { Impression, BrewFeedEntry, Coffee, Grinder } from "../types";
import { varietyKey } from "./variety";

// Shared brew-log helpers used by the coffee detail brew log and the Stats page.

// Brew ratio from coffee dose and water in, e.g. 15g → 180g = "1:12". Null unless both positive.
export function fmtRatio(dose_g: number | null, water_in_g: number | null): string | null {
  if (dose_g && water_in_g && dose_g > 0 && water_in_g > 0) {
    return `1:${(water_in_g / dose_g).toFixed(1)}`;
  }
  return null;
}

// Prefer a numeric micron reading over the free-form grind label when available.
export function grindLabel(e: { grind: string | null; grind_microns: number | null }): string | null {
  if (e.grind_microns != null) return `${e.grind_microns}µm`;
  return e.grind || null;
}

// Compact human-readable parameter chips for one brew, in a consistent order.
export function brewParamChips(e: Impression): string[] {
  const chips: string[] = [];
  if (e.brew_type === "iced") chips.push("Iced");
  else if (e.brew_type === "hot") chips.push("Hot");
  if (e.dilution_g != null) chips.push(`+${e.dilution_g}g`);
  if (e.dripper_name) chips.push(e.dripper_name);
  if (e.grinder_name) chips.push(e.rpm != null ? `${e.grinder_name} @${e.rpm}rpm` : e.grinder_name);
  if (e.pour_structure) chips.push(`pours ${e.pour_structure}`);
  if (e.frozen === true) chips.push("from frozen");
  else if (e.frozen === false) chips.push("fresh");
  if (e.method) chips.push(e.method);
  const ratio = fmtRatio(e.dose_g, e.water_in_g);
  if (ratio) chips.push(ratio);
  if (e.water_in_g != null || e.water_out_g != null) {
    chips.push(`${e.water_in_g ?? "?"}→${e.water_out_g ?? "?"}g`);
  }
  const grind = grindLabel(e);
  if (grind) chips.push(grind);
  if (e.temp_c != null) chips.push(`${e.temp_c}°C`);
  const time = fmtMinSec(e.time_s);
  if (time) chips.push(time);
  return chips;
}

// Time is entered/shown as m.s (e.g. "2.30" = 2 min 30 s) but stored as whole seconds.
// A value with no dot is read as minutes ("3" → 3:00); digits after the dot are seconds.
export function parseMinSec(v: string): number | undefined {
  const s = v.trim();
  if (s === "") return undefined;
  const [m, sec] = s.split(".");
  const mins = parseInt(m || "0", 10) || 0;
  const secs = sec ? parseInt(sec.slice(0, 2), 10) || 0 : 0;
  return mins * 60 + secs;
}
export function fmtMinSec(totalSec: number | null): string | null {
  if (totalSec == null) return null;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}.${String(s).padStart(2, "0")}`;
}

// Variety equality for recipe pooling — canonicalizes synonyms (Geisha/Gesha)
// and casing so brews of the same cultivar inform each other. See lib/variety.
const normVariety = varietyKey;

export interface RecipeSuggestion {
  basis: string;      // e.g. "Washed Geisha", "Washed", "Geisha"
  count: number;      // distinct similar coffees the suggestion draws on
  best: BrewFeedEntry; // the highest-scoring similar brew
}

// Cold-start recipe intelligence: for a coffee not yet brewed, suggest a starting recipe
// from the owner's highest-scoring past brews of similar coffees. Fallback ladder:
// same variety+process → same process → same variety. Only scored brews of *other* coffees
// count. Returns null when there's nothing comparable.
export function suggestRecipe(feed: BrewFeedEntry[], coffee: Coffee): RecipeSuggestion | null {
  const scored = feed.filter((e) => e.coffee_id !== coffee.id && e.score != null);
  if (scored.length === 0) return null;

  const sameVP = scored.filter(
    (e) => normVariety(e.variety) === normVariety(coffee.variety) && e.process === coffee.process
  );
  const sameP = scored.filter((e) => e.process === coffee.process);
  const sameV = scored.filter((e) => normVariety(e.variety) === normVariety(coffee.variety));

  const pick =
    sameVP.length ? { set: sameVP, basis: `${coffee.process} ${coffee.variety}` } :
    sameP.length  ? { set: sameP,  basis: coffee.process } :
    sameV.length  ? { set: sameV,  basis: coffee.variety } :
    null;
  if (!pick) return null;

  const best = pick.set.reduce((a, b) => ((b.score ?? 0) > (a.score ?? 0) ? b : a));
  const count = new Set(pick.set.map((e) => e.coffee_id)).size;
  return { basis: pick.basis, count, best };
}

// Starting-grind suggestion from a grinder's heuristic (COFFEE_PHYSICS §3/§12): base = washed, fresh bag,
// plus offsets for ground-from-frozen, natural process, and a small-bean coffee. It is a starting prior only —
// the drawdown then corrects it. Null when the grinder has no heuristic (grind labels are grinder-specific).
export interface GrindSuggestion {
  value: number;
  parts: string[]; // e.g. ["11.5 base", "+1 frozen"]
}

// Share of the grinder's natural offset a process gets: natural 1, honey ½ (owner: "in between natural and
// washed"), washed/other 0. Checked in that order, so "honey natural" counts as natural.
export function naturalFactor(process: string | null | undefined): number {
  const p = process || "";
  if (/natural/i.test(p)) return 1;
  if (/honey/i.test(p)) return 0.5;
  return 0;
}

export function suggestGrind(
  g: Grinder | undefined,
  opts: { frozen: boolean; natural: number; smallBean: boolean } // natural = naturalFactor(process)
): GrindSuggestion | null {
  if (!g || g.grind_base == null) return null;
  const signed = (n: number) => (n > 0 ? `+${n}` : `${n}`);
  let value = g.grind_base;
  const parts = [`${g.grind_base} base`];
  const add = (on: boolean, off: number | null, label: string) => {
    if (on && off) { value += off; parts.push(`${signed(off)} ${label}`); }
  };
  add(opts.frozen, g.off_frozen, "frozen");
  if (opts.natural === 1) add(true, g.off_natural, "natural");
  else if (opts.natural > 0 && g.off_natural) add(true, g.off_natural * opts.natural, "honey");
  add(opts.smallBean, g.off_small_bean, "small bean");
  return { value: Math.round(value * 100) / 100, parts };
}
